// Aba PDF Edit (0.50, grátis): todas as ferramentas de PDF num lugar só, feitas no próprio aparelho.
// Juntar, dividir, organizar, girar, converter, assinar, carimbo, marca d'água, tarja, números de página,
// formulários, comprimir, OCR e IA. O resultado vai para o celular, o Google Drive ou o OneDrive.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Ditado } from '../components/Ditado';
import { ActivityIndicator, Alert, Linking, Modal, Pressable, ScrollView, Switch, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { Btn, Chip, Field, Screen, Section, Sheet } from '../components/ui';
import { C, alpha, useCores } from '../components/theme';
import { AcoesItem } from '../components/Acoes';
import { Ordenavel } from '../components/Arrastar';
import { Arquivados, useSelecao } from '../components/Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { oferecerDesfazer } from '../lib/desfazer';
import { useBotaoNovo } from '../lib/botaoNovo';
import { semBloqueio } from '../auth/AuthContext';
import { abrirComApp } from '../lib/abrirArquivo';
import { RelatoriosGerados } from '../components/RelatoriosGerados';
import { IconePdfEdit } from '../components/IconeAba';
import { GuardarEm } from '../components/GuardarEm';
import { DesenhoSheet, DesenhoView } from '../components/Desenho';
import { DesenharTarjas, LeitorPdf, OrganizarPaginas, PaginaPdf } from '../components/PdfVisual';
import { useDoc } from '../store/store';
import type { PdfArq, Traco } from '../store/types';
import { br, brYmd, hm, norm } from '../lib/date';
import { t, N_ } from '../lib/i18n';
import { useIAPronta } from '../lib/ia';
import { NOME } from '../lib/nuvem';
import { escolherImagens, escolherQualquer, lerArquivo } from '../lib/pdfArquivos';
import { montarPdf } from '../lib/pdf';
import { codificar, decodificar, diminuir, ehJpeg, ehPng } from '../lib/scanImagem';
import { temTratamento } from '../lib/scanArquivos';
import * as E from '../lib/pdfEdit';
import * as F from '../lib/pdfFerramentas';
import type { Saida } from '../lib/pdfFerramentas';
import { useCaixas } from '../lib/caixas';
import { ROLAGEM_TECLADO } from '../components/teclado';

const falha = (e: any) => Alert.alert(t('Não foi possível'), traduzir(e));
function traduzir(e: any) {
  const m = String(e?.message ?? e);
  if (m === 'paginas') return t('Escolha pelo menos uma página que exista no PDF (ex.: 1-3, 5).');
  if (m === 'todas') return t('Não dá para tirar todas as páginas.');
  if (m === 'assinatura') return t('Desenhe a assinatura primeiro.');
  if (/encrypt/i.test(m)) return t('Este PDF está protegido com senha. Tire a senha no programa onde ele foi criado e tente de novo.');
  return m;
}
const tamanhoTxt = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const paginasTxt = (n: number) => (n === 1 ? t('1 página') : t('{n} páginas', { n }));

// ─── as ferramentas ───
type Grupo = 'organizar' | 'paraPdf' | 'dePdf' | 'seguranca' | 'editar' | 'avancado';
const GRUPOS: { id: Grupo; nome: string; cor: string }[] = [
  { id: 'organizar', nome: N_('Organizar'), cor: '#6366F1' },
  { id: 'paraPdf', nome: N_('Converter para PDF'), cor: '#10B981' },
  { id: 'dePdf', nome: N_('Converter de PDF'), cor: '#0EA5E9' },
  { id: 'seguranca', nome: N_('Assinatura e segurança'), cor: '#EF4444' },
  { id: 'editar', nome: N_('Ver e editar'), cor: '#F59E0B' },
  { id: 'avancado', nome: N_('Avançado'), cor: '#EC4899' },
];
type Entrada = 'um' | 'varios' | 'nenhum';
type Def = { id: string; g: Grupo; icone: string; nome: string; desc: string; entrada: Entrada; ver?: boolean; ia?: boolean };
const FERRAMENTAS: Def[] = [
  { id: 'juntar', g: 'organizar', icone: '🔗', nome: N_('Juntar PDFs'), desc: N_('Vários PDFs num só, na ordem que você escolher.'), entrada: 'varios' },
  { id: 'dividir', g: 'organizar', icone: '✂️', nome: N_('Dividir'), desc: N_('Uma página por arquivo, a cada N páginas ou por faixas.'), entrada: 'um' },
  { id: 'extrair', g: 'organizar', icone: '📤', nome: N_('Extrair páginas'), desc: N_('Um PDF novo só com as páginas escolhidas.'), entrada: 'um' },
  { id: 'remover', g: 'organizar', icone: '🗑️', nome: N_('Remover páginas'), desc: N_('Tira as páginas que você não quer.'), entrada: 'um' },
  { id: 'organizar', g: 'organizar', icone: '🧩', nome: N_('Organizar páginas'), desc: N_('Arraste para mudar a ordem, gire, duplique, apague e ponha página em branco.'), entrada: 'um', ver: true },
  { id: 'girar', g: 'organizar', icone: '🔄', nome: N_('Girar'), desc: N_('Gira todas as páginas ou só as escolhidas.'), entrada: 'um' },
  { id: 'layout', g: 'organizar', icone: '🔲', nome: N_('Várias páginas por folha'), desc: N_('2, 4, 6 ou 9 páginas em cada folha (para imprimir).'), entrada: 'um' },
  { id: 'tamanho', g: 'organizar', icone: '📐', nome: N_('Ajustar tamanho da página'), desc: N_('A4, Carta, A5 ou Ofício, sem cortar nada.'), entrada: 'um' },
  { id: 'recortar', g: 'organizar', icone: '🔳', nome: N_('Recortar margens'), desc: N_('Corta as bordas em branco ou partes da página.'), entrada: 'um' },
  { id: 'unica', g: 'organizar', icone: '📜', nome: N_('PDF numa página só'), desc: N_('Todas as páginas, uma embaixo da outra.'), entrada: 'um' },

  { id: 'imagens', g: 'paraPdf', icone: '🖼️', nome: N_('Imagens para PDF'), desc: N_('Fotos JPG, PNG e outras viram um PDF.'), entrada: 'nenhum' },
  { id: 'textoPdf', g: 'paraPdf', icone: '📝', nome: N_('Texto para PDF'), desc: N_('Escreva ou cole um texto e gere o PDF.'), entrada: 'nenhum' },
  { id: 'escanear', g: 'paraPdf', icone: '📷', nome: N_('Escanear para PDF'), desc: N_('Abre o Scanner PDF (bordas e sombras corrigidas).'), entrada: 'nenhum' },

  { id: 'paraImagens', g: 'dePdf', icone: '🌄', nome: N_('PDF para imagens'), desc: N_('Cada página vira uma imagem JPG.'), entrada: 'um', ver: true },
  { id: 'paraTexto', g: 'dePdf', icone: '🔤', nome: N_('PDF para texto'), desc: N_('Tira o texto do PDF (arquivo .txt).'), entrada: 'um', ver: true },
  { id: 'paraWord', g: 'dePdf', icone: '📄', nome: N_('PDF para Word'), desc: N_('O texto do PDF num arquivo .docx para editar.'), entrada: 'um', ver: true },

  { id: 'assinar', g: 'seguranca', icone: '✍️', nome: N_('Assinar'), desc: N_('Desenhe a assinatura e escolha a página e o lugar.'), entrada: 'um' },
  { id: 'carimbo', g: 'seguranca', icone: '🔖', nome: N_('Carimbo'), desc: N_('PAGO, RECEBIDO, CONFIDENCIAL, APROVADO ou o seu texto, com a data.'), entrada: 'um' },
  { id: 'marca', g: 'seguranca', icone: '💧', nome: N_("Marca d'água"), desc: N_('Um texto clarinho na diagonal de todas as páginas.'), entrada: 'um' },
  { id: 'ocultar', g: 'seguranca', icone: '⬛', nome: N_('Ocultar texto (tarja)'), desc: N_('Tarjas pretas sobre CPF, valores e nomes. Apaga de verdade.'), entrada: 'um', ver: true },
  { id: 'higienizar', g: 'seguranca', icone: '🧼', nome: N_('Higienizar'), desc: N_('Tira autor, programas, comandos escondidos e anexos.'), entrada: 'um' },
  { id: 'metadados', g: 'seguranca', icone: '🏷️', nome: N_('Alterar metadados'), desc: N_('Título, autor, assunto e palavras-chave.'), entrada: 'um' },

  { id: 'ver', g: 'editar', icone: '👁️', nome: N_('Ver páginas'), desc: N_('Abre o PDF para ler aqui mesmo.'), entrada: 'um', ver: true },
  { id: 'numeros', g: 'editar', icone: '🔢', nome: N_('Números de página'), desc: N_('"1", "1 / 10" ou "Página 1 de 10", no canto que quiser.'), entrada: 'um' },
  { id: 'texto', g: 'editar', icone: '✏️', nome: N_('Adicionar texto'), desc: N_('Escreva em uma ou em todas as páginas.'), entrada: 'um' },
  { id: 'imagem', g: 'editar', icone: '🖼', nome: N_('Adicionar imagem'), desc: N_('Logo, foto ou rubrica nas páginas.'), entrada: 'um' },
  { id: 'formulario', g: 'editar', icone: '📋', nome: N_('Preencher formulário'), desc: N_('Preenche os campos do PDF e, se quiser, trava.'), entrada: 'um' },
  { id: 'achatar', g: 'editar', icone: '🧱', nome: N_('Achatar'), desc: N_('Os campos do formulário viram parte da página.'), entrada: 'um' },
  { id: 'anotacoes', g: 'editar', icone: '🧽', nome: N_('Remover anotações'), desc: N_('Tira comentários, destaques e desenhos de outros programas.'), entrada: 'um' },
  { id: 'anexos', g: 'editar', icone: '📎', nome: N_('Anexar arquivos'), desc: N_('Coloca arquivos dentro do PDF.'), entrada: 'um' },
  { id: 'info', g: 'editar', icone: 'ℹ️', nome: N_('Informações do PDF'), desc: N_('Páginas, tamanho, autor, datas e campos.'), entrada: 'um' },

  { id: 'comprimir', g: 'avancado', icone: '🗜️', nome: N_('Comprimir'), desc: N_('Arquivo menor para WhatsApp e e-mail.'), entrada: 'um' },
  { id: 'efeitoScanner', g: 'avancado', icone: '📠', nome: N_('Efeito de scanner'), desc: N_('Parece que foi impresso e escaneado.'), entrada: 'um', ver: true },
  { id: 'visual', g: 'avancado', icone: '🎨', nome: N_('Ajuste visual'), desc: N_('Clareia PDFs escaneados: fundo branco, cinza ou preto e branco.'), entrada: 'um', ver: true },
  { id: 'ocr', g: 'avancado', icone: '🔎', nome: N_('OCR: texto pesquisável'), desc: N_('A IA lê as páginas e o PDF passa a aceitar busca e cópia.'), entrada: 'um', ver: true, ia: true },
  { id: 'resumir', g: 'avancado', icone: '✨', nome: N_('Resumir e perguntar'), desc: N_('A IA resume o PDF e responde perguntas.'), entrada: 'um', ia: true },
  { id: 'renomear', g: 'avancado', icone: '🪄', nome: N_('Renomear com IA'), desc: N_('A IA lê o PDF e sugere um nome claro.'), entrada: 'um', ia: true },
  { id: 'reparar', g: 'avancado', icone: '🛠️', nome: N_('Reparar PDF'), desc: N_('Tenta consertar um PDF que não abre direito.'), entrada: 'um' },
];
const def = (id: string) => FERRAMENTAS.find(f => f.id === id)!;
const corDoGrupo = (g: Grupo) => GRUPOS.find(x => x.id === g)!.cor;

export default function PdfEdit({ item, onBack }: { item?: string; onBack: () => void }) {
  const { corDe } = useCores();
  const cor = corDe('pdfedit');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const { arquivos = [], guardar = 'aparelho', auto } = useDoc('pdfedit');
  const router = useRouter();
  const [busca, setBusca] = useState('');
  const [ferr, setFerr] = useState<string | null>(null);
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [escolher, setEscolher] = useState<string | null>(null);   // ferramenta esperando o PDF
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ saidas: Saida[]; arqs: (PdfArq | null)[]; msg: string | null } | null>(null);
  const [menu, setMenu] = useState<PdfArq | null>(null);
  const [verArq, setVerArq] = useState<PdfArq | null>(null);
  const [renomear, setRenomear] = useState<PdfArq | null>(null);
  // 0.65: o arquivo próprio da aba (campo "arquivado") continua; fixar no topo, seleção, lixeira e o + flutuante
  const org = useOrganizar('pdfedit');
  const naoArq = arquivos.filter(a => !a.arquivado), arquivados = arquivos.filter(a => a.arquivado);
  const ativos = [...naoArq.filter(a => org.fixado(a.id)), ...naoArq.filter(a => !org.fixado(a.id))];
  const visiveis = ativos.slice(0, 40);
  // texto 05 C2: "📦 Item arquivado · Desfazer" (vários de uma vez viram um aviso só)
  const arquivarComDesfazer = (id: string) => { F.arquivarArq(id, true); oferecerDesfazer('arquivar', () => F.arquivarArq(id, false)); };
  const excluirArq = (a: PdfArq) => { paraLixeira('pdfedit', 'lista', a.nome, a, { doc: 'pdfedit', campo: 'arquivos' }); F.apagarArq(a.id); };
  const sel = useSelecao({ itens: visiveis, chave: a => a.id, cor, onArquivar: ids => ids.forEach(arquivarComDesfazer),
    onExcluir: ids => ids.forEach(id => { const a = F.arqPorId(id); if (a) excluirArq(a); }) });
  useBotaoNovo({ rotulo: t('Abrir PDF'), fn: abrirPdfs });
  useEffect(() => { if (item) { const a = F.arqPorId(item); if (a) setMenu(a); } }, [item]);

  const lista = useMemo(() => {
    const q = norm(busca.trim());
    return q ? FERRAMENTAS.filter(f => norm(t(f.nome) + ' ' + t(f.desc)).includes(q)) : FERRAMENTAS;
  }, [busca]);

  async function abrirPdfs() {
    try { const novos = await F.importar(true); if (novos.length === 1) setMenu(novos[0]); }
    catch (e) { falha(e); }
  }
  function usar(id: string, arqs?: string[]) {
    const d = def(id);
    if (id === 'escanear') { router.navigate({ pathname: '/aba/[id]', params: { id: 'scanner' } } as any); return; }
    if (d.entrada === 'nenhum') { setEscolhidos([]); setFerr(id); return; }
    if (arqs?.length) { if (id === 'ver') { setVerArq(F.arqPorId(arqs[0]) ?? null); return; } setEscolhidos(arqs); setFerr(id); return; }
    setEscolher(id);
  }
  /** Termina a ferramenta: guarda os PDFs na lista e, se ligado, no destino escolhido. */
  async function concluir(saidas: Saida[], aviso?: string) {
    const arqs: (PdfArq | null)[] = [];
    let msg: string | null = aviso ?? null;
    for (const s of saidas) {
      const a = s.mime === F.MIME_PDF ? await F.adicionarArquivo(s.nome, s.bytes, ferr ?? undefined) : null;
      arqs.push(a);
      if (F.guardarSozinho()) {
        try {
          const m = a ? await F.guardarArq(a, F.destinoPdf(), true) : (await F.guardarSaida(s, F.destinoPdf(), true))?.msg ?? null;
          if (m) msg = (msg ? msg + '\n' : '') + m;
        } catch (e: any) { msg = (msg ? msg + '\n' : '') + t('Não foi possível guardar: {erro}', { erro: String(e?.message ?? e) }); }
      }
    }
    setFerr(null);
    setResultado({ saidas, arqs, msg });
  }

  const dicaNuvem = (nome: string) => t('Cada arquivo pronto vai para o {nome}. Os PDFs abertos ficam também neste dispositivo, na lista abaixo.', { nome });
  const dicaPasta = (pasta: string) => t('Na pasta "{pasta}" do celular. Cada arquivo pronto vai para lá.', { pasta });

  return (
    <View style={{ flex: 1 }}>
      <Screen title={t('PDF Edit')} onBack={onBack}>
        <Pressable onPress={abrirPdfs} style={{ backgroundColor: cor, borderRadius: 18, padding: 18, flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <Text style={{ fontSize: 32 }}>📂</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ color: '#fff', fontWeight: '800', fontSize: 18 }}>{t('Abrir PDF')}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>{t('Do celular, do Google Drive ou do OneDrive')}</Text>
          </View>
        </Pressable>

        <GuardarEm cor={cor} valor={guardar} onMudar={F.mudarDestinoPdf} titulo={t('Guardar os arquivos prontos em')} dicaNuvem={dicaNuvem} dicaPasta={dicaPasta} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 4 }}>
          <Text style={{ color: C.sub, flex: 1, fontSize: 13 }}>{t('Guardar sozinho cada arquivo pronto')}</Text>
          <Switch value={auto !== false} onValueChange={F.mudarAuto} trackColor={{ true: cor }} />
        </View>

        {!!ativos.length && <Section t={t('Meus PDFs')} cor={cor} />}
        {!!ativos.length && <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>{t('Toque para abrir. Arraste para a esquerda para arquivar ou duplicar, ou para a direita para excluir.')}</Text>}
        {sel.barra}
        <Ordenavel itens={visiveis} chave={a => a.id} gap={cx.gap(10)} desligado onOrdem={() => {}} selecao={sel.prop} render={a => (
          <AcoesItem lembrar={{ aba: 'pdfedit', id: a.id, titulo: a.nome }} nome={a.nome} onExcluir={() => excluirArq(a)} onArquivar={() => arquivarComDesfazer(a.id)} onDuplicar={() => { F.duplicarArq(a).catch(falha); }}
            fixar={{ on: org.fixado(a.id), alternar: () => org.alternarFixo(a.id) }}
            menuExtra={[{ label: '✏️ ' + t('Renomear'), onPress: () => setRenomear(a) }]}
            aviso={t('Sai só da lista deste app. A cópia guardada no Drive, no OneDrive ou na pasta do celular continua lá.')}>
            <LinhaArq a={a} onPress={() => setMenu(a)} />
          </AcoesItem>
        )} />
        <Arquivados itens={arquivados} chave={a => a.id} cor={cor} render={a => <Text style={{ color: C.text }} numberOfLines={1}>📕 {a.nome}</Text>}
          onRestaurar={a => F.arquivarArq(a.id, false)} onExcluir={a => Alert.alert(t('Excluir {nome}?', { nome: a.nome }),
            t('Sai só da lista deste app. A cópia guardada no Drive, no OneDrive ou na pasta do celular continua lá.') + '\n\n' + t('Fica 30 dias na lixeira.'), [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirArq(a) }])} />

        <RelatoriosGerados aba="pdfedit" cor={cor} />
        <Section t={t('Ferramentas')} cor={cor} />
        <Field value={busca} onChangeText={setBusca} placeholder={'🔎 ' + t('Procurar ferramenta (ex.: juntar, assinar, comprimir)')} />
        {GRUPOS.map(g => {
          const fs = lista.filter(f => f.g === g.id);
          if (!fs.length) return null;
          return (
            <View key={g.id} style={{ gap: 8 }}>
              <Text style={{ color: g.cor, fontWeight: '800', fontSize: 13, marginTop: 6 }}>{t(g.nome)}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {fs.map(f => <Bloco key={f.id} f={f} onPress={() => usar(f.id)} />)}
              </View>
            </View>
          );
        })}
        <View style={{ height: 90 }} />
      </Screen>

      {/* escolher o PDF para a ferramenta */}
      <EscolherArquivo visivel={!!escolher} varios={!!escolher && def(escolher).entrada === 'varios'} cor={cor} arquivos={arquivos}
        onFechar={() => setEscolher(null)} onAbrir={abrirPdfs}
        onOk={ids => { const f = escolher!; setEscolher(null); if (f === 'ver') { setVerArq(F.arqPorId(ids[0]) ?? null); return; } setEscolhidos(ids); setFerr(f); }} />

      {/* menu de um PDF */}
      <MenuArquivo a={menu} cor={cor} onFechar={() => setMenu(null)} setOcupado={setOcupado}
        onVer={a => { setMenu(null); setVerArq(a); }} onFerramenta={(id, a) => { setMenu(null); usar(id, [a.id]); }} />

      {/* a ferramenta */}
      {ferr && (
        <Ferramenta key={ferr + escolhidos.join()} d={def(ferr)} ids={escolhidos} cor={cor} onFechar={() => setFerr(null)}
          setOcupado={setOcupado} onPronto={concluir} />
      )}

      {/* leitor */}
      <Sheet visible={!!verArq} onClose={() => setVerArq(null)} title={verArq?.nome ?? ''}>
        {verArq?.local && (F.temRenderizador() ? <LeitorPdf uri={verArq.local.uri} total={verArq.paginas} cor={cor} /> : (
          <View style={{ padding: 20, gap: 12 }}>
            <Text style={{ color: C.sub }}>{F.precisaApkNovo()}</Text>
            <Btn cor={cor} label={t('Abrir em outro app')} onPress={() => F.compartilharArq(verArq).catch(falha)} />
          </View>
        ))}
      </Sheet>

      <Renomear a={renomear} cor={cor} onFechar={() => setRenomear(null)} />
      <Resultado r={resultado} cor={cor} onFechar={() => setResultado(null)} onUsar={(a) => { setResultado(null); setMenu(a); }} />
      <Ocupado msg={ocupado} cor={cor} />
    </View>
  );
}

/** Um PDF da lista (a data é a do aparelho, não a de Greenwich). */
function LinhaArq({ a, onPress }: { a: PdfArq; onPress: () => void }) {
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  return (
    <Pressable onPress={onPress} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12), flexDirection: 'row', alignItems: 'center', gap: cx.gap(12) }}>
      <IconePdfEdit tam={cx.icone(30)} />
      <View style={{ flex: 1 }}>
        <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>{a.nome}</Text>
        <Text style={{ color: C.sub, fontSize: 12 }}>
          {br(new Date(a.criado))} · {paginasTxt(a.paginas)} · {tamanhoTxt(a.bytes)}{a.salvo ? ' · ✓ ' + (a.salvo.onde === 'aparelho' ? t('guardado') : NOME[a.salvo.onde]) : ''}
        </Text>
      </View>
      <Text style={{ color: C.mute, fontSize: 20 }}>›</Text>
    </Pressable>
  );
}

/** Menu ⋮ do PDF › Renomear: trocar o nome. */
function Renomear({ a, cor, onFechar }: { a: PdfArq | null; cor: string; onFechar: () => void }) {
  const [v, setV] = useState('');
  useEffect(() => { if (a) setV(a.nome); }, [a]);
  const ok = () => { if (a && v.trim()) F.renomearArq(a.id, v); onFechar(); };
  return (
    <Modal visible={!!a} transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 18, gap: 12 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }}>✏️ {t('Mudar o nome')}</Text>
          <Field value={v} onChangeText={setV} autoFocus selectTextOnFocus onSubmitEditing={ok} />
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 20 }}>
            <Pressable onPress={onFechar}><Text style={{ color: C.sub }}>{t('Cancelar')}</Text></Pressable>
            <Pressable onPress={ok}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function Bloco({ f, onPress }: { f: Def; onPress: () => void }) {
  const { width } = useWindowDimensions();
  const col = width > 900 ? 4 : width > 600 ? 3 : 2;
  const larg = (Math.min(width, 1000) - 32 - (col - 1) * 8) / col;
  const c = corDoGrupo(f.g);
  const cx = useCaixas();
  return (
    <Pressable onPress={onPress} style={{ width: larg, backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12), gap: cx.gap(4), borderLeftWidth: 3, borderLeftColor: c }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <View style={{ width: cx.v(34, 32, 28), height: cx.v(34, 32, 28), borderRadius: 10, backgroundColor: alpha(c, 0.18), alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 18 }}>{f.icone}</Text>
        </View>
        <Text style={{ color: C.text, fontWeight: '700', flex: 1, fontSize: 13 }} numberOfLines={2}>{t(f.nome)}</Text>
      </View>
      {cx.resumo && <Text style={{ color: C.sub, fontSize: 11 }} numberOfLines={3}>{t(f.desc)}</Text>}
    </Pressable>
  );
}

// ─── escolher PDFs ───
function EscolherArquivo({ visivel, varios, cor, arquivos, onFechar, onAbrir, onOk }: {
  visivel: boolean; varios: boolean; cor: string; arquivos: PdfArq[]; onFechar: () => void; onAbrir: () => Promise<void>; onOk: (ids: string[]) => void;
}) {
  const [sel, setSel] = useState<string[]>([]);
  const cx = useCaixas();
  useEffect(() => { if (visivel) setSel([]); }, [visivel]);
  const tocar = (id: string) => { if (!varios) return onOk([id]); setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id])); };
  return (
    <Sheet visible={visivel} onClose={onFechar} title={varios ? t('Escolha os PDFs') : t('Escolha o PDF')}
      right={varios ? <Pressable disabled={sel.length < 2} onPress={() => onOk(sel)}><Text style={{ color: sel.length >= 2 ? cor : C.mute, fontWeight: '800' }}>{t('Seguir')} ({sel.length})</Text></Pressable> : undefined}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 40 }}>
        <Btn cor={cor} label={'📂 ' + t('Abrir PDF do celular, Drive ou OneDrive')} onPress={onAbrir} />
        {varios && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque nos PDFs na ordem em que devem ficar.')}</Text>}
        {!arquivos.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 20 }}>{t('Abra um PDF para começar.')}</Text>}
        {arquivos.map(a => {
          const n = sel.indexOf(a.id);
          return (
            <Pressable key={a.id} onPress={() => tocar(a.id)} style={{ backgroundColor: n >= 0 ? alpha(cor, 0.18) : C.card, borderRadius: cx.raio(14), ...cx.pads(12), flexDirection: 'row', gap: 10, alignItems: 'center' }}>
              <Text style={{ width: 24, color: cor, fontWeight: '800' }}>{n >= 0 ? n + 1 : '📕'}</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={2}>{a.nome}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }}>{paginasTxt(a.paginas)} · {tamanhoTxt(a.bytes)}</Text>
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
    </Sheet>
  );
}

// ─── menu de um PDF da lista ───
function MenuArquivo({ a, cor, onFechar, setOcupado, onVer, onFerramenta }: {
  a: PdfArq | null; cor: string; onFechar: () => void; setOcupado: (s: string | null) => void; onVer: (a: PdfArq) => void; onFerramenta: (id: string, a: PdfArq) => void;
}) {
  const [nome, setNome] = useState('');
  const [aqui, setAqui] = useState(true);
  useEffect(() => { if (a) { setNome(a.nome); F.estaAquiArq(a).then(setAqui).catch(() => setAqui(false)); } }, [a]);
  if (!a) return null;
  const salvarNome = () => { if (nome.trim() && nome.trim() !== a.nome) F.renomearArq(a.id, nome); };
  const guardar = async () => {
    setOcupado(t('Guardando...'));
    try { const m = await F.guardarArq(a); setOcupado(null); if (m) Alert.alert(t('Pronto'), m); } catch (e) { setOcupado(null); falha(e); }
  };
  const atalhos = ['ver', 'organizar', 'assinar', 'comprimir', 'dividir', 'numeros', 'carimbo', 'ocultar', 'paraWord', 'resumir'];
  return (
    <Sheet visible={!!a} onClose={() => { salvarNome(); onFechar(); }} title={t('PDF')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 40 }}>
        <TextInput value={nome} onChangeText={setNome} onBlur={salvarNome} style={{ color: C.text, fontSize: 18, fontWeight: '800', backgroundColor: C.card, borderRadius: 12, padding: 12 }} />
        <Text style={{ color: C.sub, fontSize: 12 }}>{paginasTxt(a.paginas)} · {tamanhoTxt(a.bytes)} · {br(new Date(a.criado))} {hm(new Date(a.criado))}</Text>
        {!aqui && (
          <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 12, padding: 12, gap: 8 }}>
            <Text style={{ color: C.text }}>📱 {t('Este PDF está no dispositivo onde foi aberto.')}</Text>
            {!!a.salvo?.url && <Btn cor={cor} label={t('Abrir a cópia guardada')} onPress={() => Linking.openURL(a.salvo!.url!)} />}
          </View>
        )}
        {a.salvo && (
          <Text style={{ color: C.sub, fontSize: 12 }}>✓ {t('Guardado {onde} em {quando}', { onde: a.salvo.onde === 'aparelho' ? (a.salvo.pasta ? t('na pasta "{pasta}"', { pasta: a.salvo.pasta }) : t('neste dispositivo')) : t('no {nome}', { nome: NOME[a.salvo.onde] }),
            quando: `${br(new Date(a.salvo.em))} ${hm(new Date(a.salvo.em))}` })}</Text>
        )}
        {aqui && (
          <>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Btn cor={cor} label={'💾 ' + t('Guardar')} onPress={guardar} style={{ flex: 1 }} />
              <Btn outline cor={cor} label={'📤 ' + t('Compartilhar')} onPress={() => F.compartilharArq(a).catch(falha)} style={{ flex: 1 }} />
            </View>
            <Btn outline cor={cor} label={'↗ ' + t('Abrir com outro app')} onPress={() => semBloqueio(() => abrirComApp(a.local!.uri, F.MIME_PDF, a.nome)).catch(falha)} />
            <Btn outline cor={cor} label={'⧉ ' + t('Duplicar')} onPress={() => { salvarNome(); F.duplicarArq(a).then(() => { onFechar(); Alert.alert(t('Pronto'), t('Cópia criada.')); }).catch(falha); }} />
            <Text style={{ color: C.sub, fontSize: 12, marginTop: 6 }}>{t('Fazer com este PDF')}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {atalhos.map(id => <Chip key={id} small cor={cor} on={false} label={`${def(id).icone} ${t(def(id).nome)}`} onPress={() => (id === 'ver' ? onVer(a) : onFerramenta(id, a))} />)}
            </View>
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Todas as ferramentas ficam na tela da aba.')}</Text>
          </>
        )}
      </ScrollView>
    </Sheet>
  );
}

// ─── controles das ferramentas ───
const Rotulo = ({ children }: { children: ReactNode }) => <Text style={{ color: C.sub, fontSize: 12, marginTop: 6 }}>{children}</Text>;
function Opcoes<T extends string | number>({ valor, opcoes, cor, onMudar }: { valor: T; opcoes: [T, string][]; cor: string; onMudar: (v: T) => void }) {
  return <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>{opcoes.map(([v, l]) => <Chip key={String(v)} small cor={cor} on={valor === v} label={l} onPress={() => onMudar(v)} />)}</View>;
}
const NOMES_POS: Record<E.Posicao, string> = { 'sup-esq': '↖', 'sup-centro': '↑', 'sup-dir': '↗', 'meio-esq': '←', centro: '•', 'meio-dir': '→', 'inf-esq': '↙', 'inf-centro': '↓', 'inf-dir': '↘' };
function Posicoes({ valor, cor, onMudar }: { valor: E.Posicao; cor: string; onMudar: (p: E.Posicao) => void }) {
  return (
    <View style={{ width: 150, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {E.POSICOES.map(p => (
        <Pressable key={p} onPress={() => onMudar(p)} style={{ width: 44, height: 44, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: valor === p ? cor : C.card }}>
          <Text style={{ color: valor === p ? '#fff' : C.sub, fontSize: 18 }}>{NOMES_POS[p]}</Text>
        </Pressable>
      ))}
    </View>
  );
}
const CORES = ['#DC2626', '#16A34A', '#2563EB', '#111827', '#F59E0B', '#7C3AED'];
function Cores({ valor, onMudar }: { valor: string; onMudar: (c: string) => void }) {
  return <View style={{ flexDirection: 'row', gap: 10 }}>{CORES.map(c => <Pressable key={c} onPress={() => onMudar(c)} style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: c, borderWidth: valor === c ? 3 : 0, borderColor: '#fff' }} />)}</View>;
}
/** Páginas: todas ou só algumas ("1-3, 5"). */
function Paginas({ total, valor, cor, onMudar, rotulo }: { total: number; valor: string; cor: string; onMudar: (s: string) => void; rotulo?: string }) {
  const todas = valor === '';
  return (
    <View style={{ gap: 6 }}>
      <Rotulo>{rotulo ?? t('Páginas')}</Rotulo>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        <Chip small cor={cor} on={todas} label={t('Todas ({n})', { n: total })} onPress={() => onMudar('')} />
        <Chip small cor={cor} on={valor === '1'} label={t('Primeira')} onPress={() => onMudar('1')} />
        <Chip small cor={cor} on={valor === String(total)} label={t('Última')} onPress={() => onMudar(String(total))} />
        <Chip small cor={cor} on={!todas && valor !== '1' && valor !== String(total)} label={t('Escolher')} onPress={() => onMudar(' ')} />
      </View>
      {!todas && valor !== '1' && valor !== String(total) && (
        <Field value={valor.trim()} onChangeText={v => onMudar(v || ' ')} placeholder={t('Ex.: 1-3, 5, 8-10')} keyboardType="numbers-and-punctuation" />
      )}
    </View>
  );
}
const paginasDe = (spec: string, total: number) => (spec.trim() === '' ? Array.from({ length: total }, (_, i) => i) : E.lerPaginas(spec, total));
const hoje = () => br(new Date());

// ─── a tela de cada ferramenta ───
function Ferramenta({ d, ids, cor, onFechar, setOcupado, onPronto }: {
  d: Def; ids: string[]; cor: string; onFechar: () => void; setOcupado: (s: string | null) => void; onPronto: (s: Saida[], aviso?: string) => Promise<void>;
}) {
  const temIA = useIAPronta();
  const arqs = ids.map(F.arqPorId).filter(Boolean) as PdfArq[];
  const a = arqs[0];
  const total = a?.paginas ?? 0;
  const c = corDoGrupo(d.g);
  // estado das opções (cada ferramenta usa o que precisa)
  const [ordem, setOrdem] = useState(ids);
  const [pags, setPags] = useState('');
  const [modoDiv, setModoDiv] = useState<'cada' | 'n' | 'faixas'>('cada');
  const [n, setN] = useState('2');
  const [faixas, setFaixas] = useState('');
  const [graus, setGraus] = useState(90);
  const [porFolha, setPorFolha] = useState<2 | 4 | 6 | 9>(2);
  const [papel, setPapel] = useState<'a4' | 'carta' | 'a5' | 'oficio'>('a4');
  const [marg, setMarg] = useState({ sup: '5', inf: '5', esq: '5', dir: '5' });
  const [tamFoto, setTamFoto] = useState<'a4' | 'carta' | 'foto'>('a4');
  const [margem, setMargem] = useState(20);
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const [qual, setQual] = useState<'alta' | 'media'>('media');
  const [tracos, setTracos] = useState<{ tracos: Traco[]; alt: number; larg: number } | null>(null);
  const [desenhar, setDesenhar] = useState(false);
  const [pos, setPos] = useState<E.Posicao>('inf-dir');
  const [comData, setComData] = useState(true);
  const [carimboTxt, setCarimboTxt] = useState('PAGO');
  const [corEsc, setCorEsc] = useState('#DC2626');
  const [opac, setOpac] = useState(0.18);
  const [tarjas, setTarjas] = useState<E.Tarja[]>([]);
  const [deVerdade, setDeVerdade] = useState(true);
  const [anot, setAnot] = useState(false);
  const [anexosFora, setAnexosFora] = useState(true);
  const [meta, setMeta] = useState<E.Meta | null>(null);
  const [fmt, setFmt] = useState<E.FormatoNumero>('n/t');
  const [inicio, setInicio] = useState('1');
  const [pular, setPular] = useState(false);
  const [tam, setTam] = useState(14);
  const [negrito, setNegrito] = useState(false);
  const [img, setImg] = useState<E.ImgPdf | null>(null);
  const [largImg, setLargImg] = useState(0.25);
  const [campos, setCampos] = useState<E.Campo[] | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [travar, setTravar] = useState(true);
  const [info, setInfo] = useState<E.Info | null>(null);
  const [nivel, setNivel] = useState<F.Nivel>('recomendada');
  const [filtro, setFiltro] = useState<'documento' | 'cinza' | 'pb'>('documento');
  const [resumo, setResumo] = useState('');
  const [pergunta, setPergunta] = useState('');
  const [resposta, setResposta] = useState('');
  const [sugestao, setSugestao] = useState('');
  const [org, setOrg] = useState<{ p: number | null; giro: number }[]>([]);
  const [anexos, setAnexos] = useState<{ nome: string; bytes: Uint8Array; mime: string }[]>([]);

  // dados que a tela precisa carregar antes
  useEffect(() => {
    if (!a) return;
    if (d.id === 'metadados') F.bytesDe(a).then(E.lerMeta).then(setMeta).catch(falha);
    if (d.id === 'formulario') F.bytesDe(a).then(E.camposDoFormulario).then(cs => { setCampos(cs); setValores(Object.fromEntries(cs.map(x => [x.nome, x.valor]))); }).catch(falha);
    if (d.id === 'info') F.bytesDe(a).then(E.info).then(setInfo).catch(falha);
    if (d.id === 'carimbo') setPos('sup-dir');
    if (d.id === 'numeros') setPos('inf-centro');
    if (d.id === 'texto') setPos('sup-esq');
    if (d.id === 'imagem') setPos('sup-dir');
  }, [d.id, a?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const prog = (rot: string) => (i: number, tot: number) => setOcupado(`${rot} ${i + 1}/${tot}`);
  const precisaVer = !!d.ver && !F.temRenderizador() && !['paraTexto', 'paraWord', 'ocr'].includes(d.id);
  const precisaIA = !!d.ia && !temIA;
  const nomeSaida = (sufixo: string) => `${a ? a.nome : titulo || t('Documento')} (${sufixo})`;
  const pdf = (bytes: Uint8Array, sufixo: string): Saida => ({ nome: nomeSaida(sufixo), mime: F.MIME_PDF, bytes, ext: '.pdf' });

  async function escolherImagensParaPdf(): Promise<E.ImgPdf[]> {
    const es = await escolherImagens();
    const out: E.ImgPdf[] = [];
    for (const e of es) {
      let b = await lerArquivo(e.uri);
      if (!ehJpeg(b) && !ehPng(b)) {
        // HEIC, WEBP e outros: o celular converte para JPEG
        if (!temTratamento()) throw new Error(t('"{nome}" não é JPG nem PNG. Converta para JPG e tente de novo.', { nome: e.nome }));
        const IM = require('expo-image-manipulator');
        const r = await IM.manipulateAsync(e.uri, [], { compress: 0.88, format: IM.SaveFormat.JPEG });
        b = await lerArquivo(r.uri);
      } else if (ehJpeg(b) && b.length > 4 * 1024 * 1024) {
        try { b = codificar(diminuir(decodificar(b), 2400), 85); } catch { /* usa como veio */ }
      }
      out.push({ bytes: b, png: ehPng(b) });
    }
    return out;
  }

  async function executar() {
    try {
      const b = a ? await F.bytesDe(a) : new Uint8Array();
      const P = (s = pags) => paginasDe(s, total);
      setOcupado(t('Trabalhando...'));
      let saidas: Saida[] = [];
      let aviso: string | undefined;
      switch (d.id) {
        case 'juntar': {
          const bs = []; for (const id of ordem) bs.push(await F.bytesDe(F.arqPorId(id)!));
          saidas = [{ nome: t('PDFs juntados {data}', { data: hoje().replace(/\//g, '-') }), mime: F.MIME_PDF, bytes: await E.juntar(bs), ext: '.pdf' }];
          break;
        }
        case 'dividir': {
          const r = await E.dividir(b, modoDiv === 'cada' ? { tipo: 'cada' } : modoDiv === 'n' ? { tipo: 'n', n: +n || 1 } : { tipo: 'faixas', spec: faixas });
          if (r.length > 60) throw new Error(t('São mais de 60 arquivos. Use "A cada N páginas".'));
          saidas = r.map(x => ({ nome: `${a.nome} (${x.rotulo})`, mime: F.MIME_PDF, bytes: x.bytes, ext: '.pdf' }));
          break;
        }
        case 'extrair': saidas = [pdf(await E.extrair(b, P()), t('páginas {p}', { p: pags.trim() || t('todas') }))]; break;
        case 'remover': saidas = [pdf(await E.remover(b, P()), t('sem as páginas {p}', { p: pags.trim() }))]; break;
        case 'organizar': saidas = [pdf(await E.reorganizar(b, org), t('organizado'))]; break;
        case 'girar': saidas = [pdf(await E.girar(b, pags.trim() ? P() : null, graus), t('girado'))]; break;
        case 'layout': saidas = [pdf(await E.layout(b, porFolha), t('{n} por folha', { n: porFolha }))]; break;
        case 'tamanho': saidas = [pdf(await E.tamanhoPagina(b, papel === 'a4' ? E.A4 : papel === 'carta' ? E.CARTA : papel === 'a5' ? E.A5 : E.OFICIO), papel.toUpperCase())]; break;
        case 'recortar': saidas = [pdf(await E.recortar(b, { sup: +marg.sup || 0, inf: +marg.inf || 0, esq: +marg.esq || 0, dir: +marg.dir || 0 }, pags.trim() ? P() : null), t('recortado'))]; break;
        case 'unica': saidas = [pdf(await E.paginaUnica(b), t('página única'))]; break;
        case 'imagens': {
          const ims = await escolherImagensParaPdf();
          if (!ims.length) { setOcupado(null); return; }
          saidas = [{ nome: titulo || t('Imagens {data}', { data: hoje().replace(/\//g, '-') }), mime: F.MIME_PDF, bytes: await E.imagensParaPdf(ims, tamFoto, margem), ext: '.pdf' }];
          break;
        }
        case 'textoPdf': {
          if (!texto.trim()) throw new Error(t('Escreva o texto.'));
          const blocos = texto.split('\n').map(l => (l.startsWith('# ') ? { t: 'titulo' as const, texto: l.slice(2) } : l.startsWith('## ') ? { t: 'par' as const, texto: l.slice(3), negrito: true } : l.trim() ? { t: 'par' as const, texto: l } : { t: 'espaco' as const }));
          saidas = [{ nome: titulo || t('Texto {data}', { data: hoje().replace(/\//g, '-') }), mime: F.MIME_PDF, bytes: await montarPdf(titulo ? [{ t: 'titulo', texto: titulo }, ...blocos] : blocos), ext: '.pdf' }];
          break;
        }
        case 'paraImagens': saidas = [await F.paraImagens(a, qual, prog(t('Página')))]; break;
        case 'paraTexto': saidas = [await F.paraTexto(a, prog(t('Lendo a página')))]; break;
        case 'paraWord': saidas = [await F.paraWord(a, prog(t('Lendo a página')))]; break;
        case 'assinar': if (!tracos) throw new Error('assinatura'); saidas = [pdf(await E.assinar(b, tracos.tracos, P(), pos, 0.3, comData ? hoje() : undefined), t('assinado'))]; break;
        case 'carimbo': saidas = [pdf(await E.carimbo(b, carimboTxt || 'OK', corEsc, P(), pos, comData ? hoje() : undefined), carimboTxt.toLowerCase())]; break;
        case 'marca': if (!texto.trim()) throw new Error(t('Escreva o texto da marca d\'água.')); saidas = [pdf(await E.marcaDagua(b, texto, corEsc, opac), t("marca d'água"))]; break;
        case 'ocultar': {
          if (!tarjas.length) throw new Error(t('Desenhe pelo menos uma tarja.'));
          const comTarja = await E.tarjas(b, tarjas);
          if (deVerdade) {
            // a página vira imagem: o texto por baixo da tarja some de verdade (não dá para copiar nem buscar)
            const tmp = await F.adicionarArquivo(a.nome + ' (tmp)', comTarja, 'tmp');
            try {
              const ps = new Set(tarjas.map(x => x.pagina));
              const ras = await F.rasterizar(tmp, { maxLado: 2000, qualidade: 82 }, prog(t('Página')));
              // junta: páginas com tarja (imagem) + as outras como estavam
              const dr = await E.carregar(ras), dc = await E.carregar(comTarja);
              const out = await E.novo();
              for (let i = 0; i < dc.getPageCount(); i++) { const [p] = await out.copyPages(ps.has(i) ? dr : dc, [i]); out.addPage(p); }
              saidas = [pdf(await out.save(), t('com tarjas'))];
            } finally { F.apagarArq(tmp.id); }
          } else saidas = [pdf(comTarja, t('com tarjas'))];
          break;
        }
        case 'higienizar': saidas = [pdf(await E.higienizar(b, anot, anexosFora), t('higienizado'))]; break;
        case 'metadados': if (!meta) return; saidas = [pdf(await E.gravarMeta(b, meta), t('metadados'))]; break;
        case 'numeros': saidas = [pdf(await E.numerarPaginas(b, { formato: fmt, pos, inicio: +inicio || 1, pularPrimeira: pular, tamanho: 10, palavraPagina: t('Página'), palavraDe: t('de') }), t('numerado'))]; break;
        case 'texto': if (!texto.trim()) throw new Error(t('Escreva o texto.')); saidas = [pdf(await E.adicionarTexto(b, P(), texto, pos, tam, corEsc, negrito), t('com texto'))]; break;
        case 'imagem': if (!img) throw new Error(t('Escolha a imagem.')); saidas = [pdf(await E.adicionarImagem(b, img, P(), pos, largImg), t('com imagem'))]; break;
        case 'formulario': saidas = [pdf(await E.preencher(b, valores, travar), t('preenchido'))]; break;
        case 'achatar': saidas = [pdf(await E.achatar(b), t('achatado'))]; break;
        case 'anotacoes': saidas = [pdf(await E.removerAnotacoes(b, true), t('sem anotações'))]; break;
        case 'anexos': if (!anexos.length) throw new Error(t('Escolha os arquivos.')); saidas = [pdf(await E.anexar(b, anexos), t('com anexos'))]; break;
        case 'comprimir': {
          const r = await F.comprimir(a, nivel, prog(t('Página')));
          if (r.length >= b.length) { setOcupado(null); Alert.alert(t('Já está compacto'), t('Não deu para diminuir mais este PDF ({tam}).', { tam: tamanhoTxt(b.length) })); return; }
          aviso = t('De {de} para {para} ({p}% menor).', { de: tamanhoTxt(b.length), para: tamanhoTxt(r.length), p: Math.round((1 - r.length / b.length) * 100) });
          saidas = [pdf(r, t('comprimido'))];
          break;
        }
        case 'efeitoScanner': saidas = [pdf(await F.rasterizar(a, { maxLado: 1700, qualidade: 70, tratar: F.efeitoScanner }, prog(t('Página'))), t('escaneado'))]; break;
        case 'visual': saidas = [pdf(await F.rasterizar(a, { maxLado: 2000, qualidade: 80, tratar: F.ajusteVisual(filtro) }, prog(t('Página'))), t('ajustado'))]; break;
        case 'ocr': saidas = [pdf(await F.ocr(a, prog(t('Lendo a página'))), t('pesquisável'))]; break;
        case 'resumir': setResumo(await F.resumirPdf(a, prog(t('Lendo a página')))); setOcupado(null); return;
        case 'renomear': setSugestao(await F.sugerirNome(a)); setOcupado(null); return;
        case 'reparar': saidas = [pdf(await E.regravar(b), t('reparado'))]; break;
        case 'info': setOcupado(null); return;
      }
      setOcupado(t('Guardando...'));
      await onPronto(saidas, aviso);
    } catch (e) { falha(e); } finally { setOcupado(null); }
  }
  async function perguntarIA() {
    if (!pergunta.trim()) return;
    try { setResposta(await F.perguntarPdf(a, pergunta.trim(), prog(t('Lendo a página')))); } catch (e) { falha(e); } finally { setOcupado(null); }
  }

  const botao = d.id === 'info' ? null
    : d.id === 'resumir' ? '✨ ' + t('Resumir')
    : d.id === 'renomear' ? '🪄 ' + t('Sugerir nome')
    : d.id === 'imagens' ? '🖼️ ' + t('Escolher as imagens e criar o PDF')
    : `${d.icone} ${t(d.nome)}`;

  return (
    <Sheet visible onClose={onFechar} title={t(d.nome)}>
      <ScrollView {...ROLAGEM_TECLADO} contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
        <Text style={{ color: C.sub }}>{t(d.desc)}</Text>
        {a && d.id !== 'juntar' && <Text style={{ color: C.text, fontWeight: '700' }}>📕 {a.nome} · {paginasTxt(total)}</Text>}
        {precisaVer && <Aviso cor={c} txt={F.precisaApkNovo()} />}
        {precisaIA && <Aviso cor={c} txt={t('Ligue a sua IA em Ajustes › Inteligência artificial (Claude, ChatGPT ou Gemini) para usar esta ferramenta.')} />}
        {(d.id === 'paraTexto' || d.id === 'paraWord') && <Text style={{ color: C.mute, fontSize: 12 }}>{t('PDF com texto digital: sai na hora no navegador. PDF escaneado (foto): a sua IA lê cada página.')}</Text>}

        {d.id === 'juntar' && (
          <View style={{ gap: 8 }}>
            {ordem.map((id, i) => (
              <View key={id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 12, padding: 10 }}>
                <Text style={{ color: c, fontWeight: '800', width: 22 }}>{i + 1}</Text>
                <Text style={{ color: C.text, flex: 1 }} numberOfLines={2}>{F.arqPorId(id)?.nome}</Text>
                <Pressable disabled={!i} hitSlop={8} onPress={() => setOrdem(o => { const x = [...o]; [x[i - 1], x[i]] = [x[i], x[i - 1]]; return x; })}><Text style={{ color: i ? C.text : C.mute, fontSize: 18 }}>↑</Text></Pressable>
                <Pressable disabled={i === ordem.length - 1} hitSlop={8} onPress={() => setOrdem(o => { const x = [...o]; [x[i + 1], x[i]] = [x[i], x[i + 1]]; return x; })}><Text style={{ color: i < ordem.length - 1 ? C.text : C.mute, fontSize: 18 }}>↓</Text></Pressable>
              </View>
            ))}
          </View>
        )}
        {d.id === 'dividir' && (
          <>
            <Opcoes cor={c} valor={modoDiv} onMudar={setModoDiv} opcoes={[['cada', t('Uma página por arquivo')], ['n', t('A cada N páginas')], ['faixas', t('Por faixas')]]} />
            {modoDiv === 'n' && <Field value={n} onChangeText={setN} keyboardType="number-pad" placeholder="2" />}
            {modoDiv === 'faixas' && <Field value={faixas} onChangeText={setFaixas} placeholder={t('Ex.: 1-3, 4-10, 11-')} />}
          </>
        )}
        {(d.id === 'extrair' || d.id === 'remover') && (
          <>
            <Rotulo>{d.id === 'extrair' ? t('Quais páginas ficam no PDF novo') : t('Quais páginas sair')}</Rotulo>
            <Field value={pags} onChangeText={setPags} placeholder={t('Ex.: 1-3, 5, 8-10')} keyboardType="numbers-and-punctuation" />
            {F.temRenderizador() && a?.local && <MiniPaginas uri={a.local.uri} total={total} cor={c} sel={paginasDe(pags || ' ', total)} onTocar={i => setPags(alternar(pags, i, total))} />}
          </>
        )}
        {d.id === 'organizar' && a?.local && (F.temRenderizador() || total <= 60) && <OrganizarPaginas uri={a.local.uri} total={total} cor={c} onMudar={setOrg} />}
        {d.id === 'girar' && (
          <>
            <Opcoes cor={c} valor={graus} onMudar={setGraus} opcoes={[[90, '↻ 90°'], [180, '180°'], [270, '↺ 90°']]} />
            <Paginas total={total} valor={pags} cor={c} onMudar={setPags} />
          </>
        )}
        {d.id === 'layout' && <Opcoes cor={c} valor={porFolha} onMudar={setPorFolha} opcoes={[[2, t('2 por folha')], [4, t('4 por folha')], [6, t('6 por folha')], [9, t('9 por folha')]]} />}
        {d.id === 'tamanho' && <Opcoes cor={c} valor={papel} onMudar={setPapel} opcoes={[['a4', 'A4'], ['carta', t('Carta')], ['a5', 'A5'], ['oficio', t('Ofício')]]} />}
        {d.id === 'recortar' && (
          <>
            <Rotulo>{t('Quanto cortar de cada lado (% da página)')}</Rotulo>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {(['sup', 'inf', 'esq', 'dir'] as const).map(k => (
                <View key={k} style={{ flex: 1, gap: 4 }}>
                  <Text style={{ color: C.sub, fontSize: 11, textAlign: 'center' }}>{k === 'sup' ? t('Em cima') : k === 'inf' ? t('Embaixo') : k === 'esq' ? t('Esquerda') : t('Direita')}</Text>
                  <Field value={marg[k]} onChangeText={v => setMarg(m => ({ ...m, [k]: v.replace(/[^\d.]/g, '') }))} keyboardType="decimal-pad" />
                </View>
              ))}
            </View>
            <Paginas total={total} valor={pags} cor={c} onMudar={setPags} />
          </>
        )}
        {d.id === 'imagens' && (
          <>
            <Field value={titulo} onChangeText={setTitulo} placeholder={t('Nome do PDF (opcional)')} />
            <Rotulo>{t('Tamanho da página')}</Rotulo>
            <Opcoes cor={c} valor={tamFoto} onMudar={setTamFoto} opcoes={[['a4', 'A4'], ['carta', t('Carta')], ['foto', t('Tamanho da foto')]]} />
            {tamFoto !== 'foto' && <><Rotulo>{t('Margem')}</Rotulo><Opcoes cor={c} valor={margem} onMudar={setMargem} opcoes={[[0, t('Sem margem')], [20, t('Pequena')], [45, t('Grande')]]} /></>}
          </>
        )}
        {d.id === 'textoPdf' && (
          <>
            <Field value={titulo} onChangeText={setTitulo} placeholder={t('Título (opcional)')} />
            <TextInput value={texto} onChangeText={setTexto} multiline placeholder={t('Escreva ou cole o texto. Linha começando com "# " vira título.')} placeholderTextColor={C.mute}
              style={{ color: C.text, backgroundColor: C.card, borderRadius: 12, padding: 12, minHeight: 220, textAlignVertical: 'top' }} />
            <View style={{ alignItems: 'flex-end' }}><Ditado pequeno cor={C.sub} onTexto={tx => setTexto(v => (v ? v + ' ' + tx : tx))} /></View>
          </>
        )}
        {d.id === 'paraImagens' && <Opcoes cor={c} valor={qual} onMudar={setQual} opcoes={[['media', t('Qualidade boa (menor)')], ['alta', t('Qualidade alta')]]} />}
        {d.id === 'assinar' && (
          <>
            {tracos ? (
              <View style={{ width: 200, alignSelf: 'center', gap: 6 }}>
                <DesenhoView tracos={tracos.tracos} alt={tracos.alt} larg={tracos.larg} fundo="#fff" />
                <Pressable onPress={() => setDesenhar(true)}><Text style={{ color: c, textAlign: 'center', fontWeight: '700' }}>{t('Refazer')}</Text></Pressable>
              </View>
            ) : <Btn outline cor={c} label={'✍️ ' + t('Desenhar a assinatura')} onPress={() => setDesenhar(true)} />}
            <Paginas total={total} valor={pags || String(total)} cor={c} onMudar={setPags} rotulo={t('Em quais páginas')} />
            <Rotulo>{t('Onde na página')}</Rotulo><Posicoes valor={pos} cor={c} onMudar={setPos} />
            <Chip small cor={c} on={comData} label={(comData ? '✓ ' : '') + t('Pôr a data de hoje')} onPress={() => setComData(v => !v)} />
            <DesenhoSheet visible={desenhar} cor={c} alt={400} assinatura onClose={() => setDesenhar(false)} onSalvar={(tr, al, lg) => { setDesenhar(false); if (tr.length) setTracos({ tracos: tr, alt: al, larg: lg }); }} />
          </>
        )}
        {d.id === 'carimbo' && (
          <>
            <Opcoes cor={c} valor={carimboTxt} onMudar={setCarimboTxt} opcoes={(['PAGO', 'RECEBIDO', 'APROVADO', 'CONFIDENCIAL', 'CÓPIA', 'CANCELADO'] as const).map(x => [x, t(x)] as [string, string])} />
            <Field value={carimboTxt} onChangeText={setCarimboTxt} placeholder={t('Ou escreva o seu')} />
            <Rotulo>{t('Cor')}</Rotulo><Cores valor={corEsc} onMudar={setCorEsc} />
            <Rotulo>{t('Onde na página')}</Rotulo><Posicoes valor={pos} cor={c} onMudar={setPos} />
            <Paginas total={total} valor={pags || '1'} cor={c} onMudar={setPags} rotulo={t('Em quais páginas')} />
            <Chip small cor={c} on={comData} label={(comData ? '✓ ' : '') + t('Pôr a data de hoje')} onPress={() => setComData(v => !v)} />
          </>
        )}
        {d.id === 'marca' && (
          <>
            <Field value={texto} onChangeText={setTexto} placeholder={t('Ex.: CONFIDENCIAL, CÓPIA, RASCUNHO')} />
            <Rotulo>{t('Cor')}</Rotulo><Cores valor={corEsc} onMudar={setCorEsc} />
            <Rotulo>{t('Intensidade')}</Rotulo>
            <Opcoes cor={c} valor={opac} onMudar={setOpac} opcoes={[[0.1, t('Bem clara')], [0.18, t('Clara')], [0.3, t('Forte')]]} />
          </>
        )}
        {d.id === 'ocultar' && a?.local && F.temRenderizador() && (
          <>
            <DesenharTarjas uri={a.local.uri} total={total} cor={c} tarjas={tarjas} onMudar={setTarjas} />
            <Chip small cor={c} on={deVerdade} label={(deVerdade ? '✓ ' : '') + t('Apagar de verdade (as páginas com tarja viram imagem)')} onPress={() => setDeVerdade(v => !v)} />
            {!deVerdade && <Text style={{ color: C.warn, fontSize: 12 }}>{t('Sem isso, a tarja só cobre: o texto por baixo continua no arquivo e dá para copiar.')}</Text>}
          </>
        )}
        {d.id === 'higienizar' && (
          <>
            <Chip small cor={c} on={anexosFora} label={(anexosFora ? '✓ ' : '') + t('Tirar arquivos anexados')} onPress={() => setAnexosFora(v => !v)} />
            <Chip small cor={c} on={anot} label={(anot ? '✓ ' : '') + t('Tirar comentários, destaques e links')} onPress={() => setAnot(v => !v)} />
          </>
        )}
        {d.id === 'metadados' && meta && (
          <>
            {([['titulo', t('Título')], ['autor', t('Autor')], ['assunto', t('Assunto')], ['palavras', t('Palavras-chave (separe com vírgula)')]] as [keyof E.Meta, string][]).map(([k, l]) => (
              <View key={k} style={{ gap: 4 }}><Rotulo>{l}</Rotulo><Field value={meta[k]} onChangeText={v => setMeta(m => ({ ...m!, [k]: v }))} /></View>
            ))}
          </>
        )}
        {d.id === 'numeros' && (
          <>
            <Rotulo>{t('Formato')}</Rotulo>
            <Opcoes cor={c} valor={fmt} onMudar={setFmt} opcoes={[['n', '1'], ['n/t', '1 / 10'], ['pag-n', t('Página 1')], ['pag-n-de-t', t('Página 1 de 10')]]} />
            <Rotulo>{t('Onde na página')}</Rotulo><Posicoes valor={pos} cor={c} onMudar={setPos} />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: C.sub }}>{t('Começar em')}</Text>
              <View style={{ width: 80 }}><Field value={inicio} onChangeText={setInicio} keyboardType="number-pad" /></View>
            </View>
            <Chip small cor={c} on={pular} label={(pular ? '✓ ' : '') + t('Sem número na primeira página (capa)')} onPress={() => setPular(v => !v)} />
          </>
        )}
        {d.id === 'texto' && (
          <>
            <TextInput value={texto} onChangeText={setTexto} multiline placeholder={t('O texto')} placeholderTextColor={C.mute}
              style={{ color: C.text, backgroundColor: C.card, borderRadius: 12, padding: 12, minHeight: 90, textAlignVertical: 'top' }} />
            <View style={{ alignItems: 'flex-end' }}><Ditado pequeno cor={C.sub} onTexto={tx => setTexto(v => (v ? v + ' ' + tx : tx))} /></View>
            <Rotulo>{t('Tamanho')}</Rotulo>
            <Opcoes cor={c} valor={tam} onMudar={setTam} opcoes={[[10, t('Pequeno')], [14, t('Médio')], [22, t('Grande')]]} />
            <Chip small cor={c} on={negrito} label={(negrito ? '✓ ' : '') + t('Negrito')} onPress={() => setNegrito(v => !v)} />
            <Rotulo>{t('Cor')}</Rotulo><Cores valor={corEsc} onMudar={setCorEsc} />
            <Rotulo>{t('Onde na página')}</Rotulo><Posicoes valor={pos} cor={c} onMudar={setPos} />
            <Paginas total={total} valor={pags || '1'} cor={c} onMudar={setPags} rotulo={t('Em quais páginas')} />
          </>
        )}
        {d.id === 'imagem' && (
          <>
            <Btn outline cor={c} label={img ? '✓ ' + t('Imagem escolhida (trocar)') : '🖼️ ' + t('Escolher a imagem')} onPress={async () => { try { const ims = await escolherImagensParaPdf(); if (ims[0]) setImg(ims[0]); } catch (e) { falha(e); } }} />
            <Rotulo>{t('Tamanho')}</Rotulo>
            <Opcoes cor={c} valor={largImg} onMudar={setLargImg} opcoes={[[0.15, t('Pequena')], [0.25, t('Média')], [0.5, t('Grande')], [1, t('Página inteira')]]} />
            <Rotulo>{t('Onde na página')}</Rotulo><Posicoes valor={pos} cor={c} onMudar={setPos} />
            <Paginas total={total} valor={pags || '1'} cor={c} onMudar={setPags} rotulo={t('Em quais páginas')} />
          </>
        )}
        {d.id === 'formulario' && campos && (
          !campos.length ? <Text style={{ color: C.sub }}>{t('Este PDF não tem campos de formulário. Use "Adicionar texto" para escrever nele.')}</Text> : (
            <>
              {campos.map(cp => (
                <View key={cp.nome} style={{ gap: 4 }}>
                  <Rotulo>{cp.nome}</Rotulo>
                  {cp.tipo === 'texto' && <Field value={valores[cp.nome] ?? ''} onChangeText={v => setValores(x => ({ ...x, [cp.nome]: v }))} />}
                  {cp.tipo === 'marcar' && <Chip small cor={c} on={!!valores[cp.nome]} label={valores[cp.nome] ? '✓ ' + t('Marcado') : t('Desmarcado')} onPress={() => setValores(x => ({ ...x, [cp.nome]: x[cp.nome] ? '' : '1' }))} />}
                  {(cp.tipo === 'lista' || cp.tipo === 'opcao') && <Opcoes cor={c} valor={valores[cp.nome] ?? ''} onMudar={v => setValores(x => ({ ...x, [cp.nome]: v }))} opcoes={(cp.opcoes ?? []).map(o => [o, o] as [string, string])} />}
                </View>
              ))}
              <Chip small cor={c} on={travar} label={(travar ? '✓ ' : '') + t('Travar depois de preencher')} onPress={() => setTravar(v => !v)} />
            </>
          )
        )}
        {d.id === 'anexos' && (
          <>
            <Btn outline cor={c} label={'📎 ' + t('Escolher arquivos')} onPress={async () => {
              try { const es = await escolherQualquer(); const out: { nome: string; mime: string; bytes: Uint8Array }[] = []; for (const e of es) out.push({ nome: e.nome, mime: e.mime, bytes: await lerArquivo(e.uri) }); setAnexos(x => [...x, ...out]); } catch (e) { falha(e); }
            }} />
            {anexos.map((x, i) => <Text key={i} style={{ color: C.text }}>📎 {x.nome} · {tamanhoTxt(x.bytes.length)}</Text>)}
          </>
        )}
        {d.id === 'info' && info && (
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 14, gap: 6 }}>
            {([[t('Páginas'), String(info.paginas)], [t('Tamanho do arquivo'), tamanhoTxt(info.bytes)], [t('Tamanho das páginas'), info.tamanhos.join(', ')], [t('Versão do PDF'), info.versao],
              [t('Título'), info.titulo], [t('Autor'), info.autor], [t('Assunto'), info.assunto], [t('Criado com'), info.criador], [t('Gerado por'), info.produtor],
              [t('Criado em'), info.criado], [t('Modificado em'), info.modificado], [t('Campos de formulário'), String(info.campos)],
              [t('Tem anexos'), info.anexos ? t('Sim') : t('Não')], [t('Protegido'), info.protegido ? t('Sim') : t('Não')]] as [string, string][])
              .filter(([, v]) => v).map(([k, v]) => (
                <View key={k} style={{ flexDirection: 'row', gap: 10 }}><Text style={{ color: C.sub, width: 140 }}>{k}</Text><Text style={{ color: C.text, flex: 1 }} selectable>{v}</Text></View>
              ))}
          </View>
        )}
        {d.id === 'comprimir' && (
          <>
            <Opcoes cor={c} valor={nivel} onMudar={setNivel} opcoes={[['sem-perda', t('Leve (sem perder qualidade)')], ['recomendada', t('Recomendada')], ['maxima', t('Máxima')]]} />
            <Text style={{ color: C.sub, fontSize: 12 }}>
              {nivel === 'sem-perda' ? t('Reorganiza o arquivo por dentro. Ajuda pouco em PDFs com fotos.') : t('As páginas viram imagem mais leve: ótimo para PDFs escaneados. O texto deixa de ser pesquisável (use o OCR depois, se precisar).')}
            </Text>
            {nivel !== 'sem-perda' && !F.temRenderizador() && <Aviso cor={c} txt={F.precisaApkNovo()} />}
          </>
        )}
        {d.id === 'visual' && <Opcoes cor={c} valor={filtro} onMudar={setFiltro} opcoes={[['documento', t('Documento (fundo branco)')], ['cinza', t('Cinza')], ['pb', t('Preto e branco')]]} />}
        {d.id === 'resumir' && (
          <>
            {!!resumo && <Text style={{ color: C.text, backgroundColor: alpha(c, 0.12), borderRadius: 14, padding: 12 }} selectable>{resumo}</Text>}
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
              <View style={{ flex: 1 }}><Field value={pergunta} onChangeText={setPergunta} placeholder={t('Pergunte sobre o documento (ex.: qual o prazo?)')} onSubmitEditing={perguntarIA} /></View>
              <Pressable onPress={perguntarIA} disabled={!temIA} style={{ backgroundColor: c, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, opacity: temIA ? 1 : 0.4 }}><Text style={{ color: '#fff', fontWeight: '800' }}>💬</Text></Pressable>
            </View>
            {!!resposta && <Text style={{ color: C.text, backgroundColor: C.card, borderRadius: 14, padding: 12 }} selectable>{resposta}</Text>}
          </>
        )}
        {d.id === 'renomear' && !!sugestao && (
          <View style={{ backgroundColor: alpha(c, 0.12), borderRadius: 14, padding: 12, gap: 8 }}>
            <TextInput value={sugestao} onChangeText={setSugestao} style={{ color: C.text, fontWeight: '700', fontSize: 16 }} />
            <Btn cor={c} label={t('Usar este nome')} onPress={() => { F.renomearArq(a.id, sugestao); onFechar(); }} />
          </View>
        )}
        {(d.ia || ['paraTexto', 'paraWord', 'ocr'].includes(d.id)) && <Text style={{ color: C.mute, fontSize: 11 }}>{t('As páginas vão só para a IA que você escolheu, com a sua chave.')}</Text>}

        {botao && <Btn cor={c} label={botao} onPress={executar} disabled={precisaVer || precisaIA || (d.entrada !== 'nenhum' && !a)} style={{ marginTop: 10 }} />}
      </ScrollView>
    </Sheet>
  );
}

/** Escolher páginas tocando nas miniaturas. */
function MiniPaginas({ uri, total, cor, sel, onTocar }: { uri: string; total: number; cor: string; sel: number[]; onTocar: (i: number) => void }) {
  const { width } = useWindowDimensions();
  const col = width > 700 ? 6 : 4;
  const larg = Math.floor((Math.min(width, 900) - 32 - (col - 1) * 8) / col);
  const [ver, setVer] = useState(Math.min(total, 24));
  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {Array.from({ length: ver }, (_, i) => (
          <Pressable key={i} onPress={() => onTocar(i)} style={{ borderRadius: 8, borderWidth: 3, borderColor: sel.includes(i) ? cor : 'transparent', alignItems: 'center' }}>
            <PaginaPdf uri={uri} i={i} larg={larg - 6} cor={cor} maxLado={300} />
            <Text style={{ color: sel.includes(i) ? cor : C.sub, fontSize: 11, fontWeight: '700' }}>{i + 1}</Text>
          </Pressable>
        ))}
      </View>
      {ver < total && <Pressable onPress={() => setVer(v => Math.min(total, v + 24))}><Text style={{ color: cor, textAlign: 'center' }}>{t('Ver mais páginas')}</Text></Pressable>}
    </View>
  );
}
/** Liga ou desliga uma página no texto "1-3, 5". */
function alternar(spec: string, i: number, total: number) {
  const s = new Set(spec.trim() ? E.lerPaginas(spec, total) : []);
  if (s.has(i)) s.delete(i); else s.add(i);
  const l = [...s].sort((a, b) => a - b);
  const partes: string[] = [];
  for (let k = 0; k < l.length; k++) {
    let j = k; while (j + 1 < l.length && l[j + 1] === l[j] + 1) j++;
    partes.push(j > k ? `${l[k] + 1}-${l[j] + 1}` : `${l[k] + 1}`); k = j;
  }
  return partes.join(', ');
}

const Aviso = ({ cor, txt }: { cor: string; txt: string }) => <Text style={{ color: C.text, backgroundColor: alpha(cor, 0.14), borderRadius: 12, padding: 12, fontSize: 13 }}>{txt}</Text>;

// ─── resultado ───
function Resultado({ r, cor, onFechar, onUsar }: { r: { saidas: Saida[]; arqs: (PdfArq | null)[]; msg: string | null } | null; cor: string; onFechar: () => void; onUsar: (a: PdfArq) => void }) {
  if (!r) return null;
  return (
    <Sheet visible onClose={onFechar} title={t('Pronto')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 40 }}>
        {!!r.msg && <Text style={{ color: C.text, backgroundColor: alpha(cor, 0.14), borderRadius: 12, padding: 12 }}>✓ {r.msg}</Text>}
        {r.saidas.map((s, i) => (
          <View key={i} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
            <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>{s.ext === '.pdf' ? '📕' : s.ext === '.docx' ? '📄' : s.ext === '.zip' ? '🗂️' : '📝'} {s.nome}{s.ext}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{tamanhoTxt(s.bytes.length)}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              <Chip small cor={cor} on={false} label={'📤 ' + t('Compartilhar')} onPress={() => F.compartilharSaida(s).catch(falha)} />
              <Chip small cor={cor} on={false} label={'💾 ' + t('Guardar')} onPress={async () => {
                try { const g = r.arqs[i] ? await F.guardarArq(r.arqs[i]!) : (await F.guardarSaida(s))?.msg; if (g) Alert.alert(t('Pronto'), g); } catch (e) { falha(e); }
              }} />
              {r.arqs[i] && <Chip small cor={cor} on={false} label={'🧰 ' + t('Mais ferramentas')} onPress={() => onUsar(r.arqs[i]!)} />}
            </View>
          </View>
        ))}
      </ScrollView>
    </Sheet>
  );
}

function Ocupado({ msg, cor }: { msg: string | null; cor: string }) {
  return (
    <Modal visible={!!msg} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center' }}>
        <View style={{ backgroundColor: C.card, borderRadius: 18, padding: 22, gap: 12, alignItems: 'center', minWidth: 220, maxWidth: 320 }}>
          <ActivityIndicator color={cor} size="large" />
          <Text style={{ color: C.text, textAlign: 'center' }}>{msg}</Text>
        </View>
      </View>
    </Modal>
  );
}
